import { parseDateToken } from "./quickTaskParser";

const HORA_PADRAO = 9; // usada quando ha data mas nao ha hora

const PREFIXOS_RELATIVOS = ["em", "daqui", "apos", "após"];

const UNIDADES = {
  min: 1,
  mins: 1,
  m: 1,
  minuto: 1,
  minutos: 1,
  h: 60,
  hs: 60,
  hr: 60,
  hrs: 60,
  hora: 60,
  horas: 60,
  d: 1440,
  dia: 1440,
  dias: 1440,
};

/**
 * Le uma duracao colada ao numero: 30min, 2h, 3dias.
 * Devolve minutos ou null.
 */
function lerDuracaoColada(token) {
  const casa = (token || "").toLowerCase().match(/^(\d+)([a-zç]+)$/);
  if (!casa) return null;

  const quantidade = Number(casa[1]);
  const fator = UNIDADES[casa[2]];

  if (!fator || quantidade <= 0) return null;
  return quantidade * fator;
}

/**
 * Le uma duracao relativa a partir da posicao indicada.
 *
 * Formas aceitas:
 *   em 30min | em 30 min | daqui 2h | daqui 2 horas | 30min (solto)
 *
 * "min" e inequivoco, entao vale solto. Ja "2h" solto continua significando
 * 02:00 - so vira "+2 horas" quando vem depois de em/daqui.
 */
function lerDuracaoRelativa(tokens, indice) {
  const atual = (tokens[indice] || "").toLowerCase();

  if (PREFIXOS_RELATIVOS.includes(atual)) {
    const colada = lerDuracaoColada(tokens[indice + 1]);
    if (colada) return { minutos: colada, consumidos: 2 };

    // "em 30 min" - numero e unidade separados
    const quantidade = Number(tokens[indice + 1]);
    const fator = UNIDADES[(tokens[indice + 2] || "").toLowerCase()];

    if (Number.isInteger(quantidade) && quantidade > 0 && fator) {
      return { minutos: quantidade * fator, consumidos: 3 };
    }

    return null;
  }

  // Solto, apenas quando a unidade nao pode ser confundida com hora do dia.
  const colada = lerDuracaoColada(atual);
  const soMinutos = /^\d+(min|mins|minuto|minutos|m)$/i.test(atual);

  if (colada && soMinutos) return { minutos: colada, consumidos: 1 };

  return null;
}

/**
 * Le a hora de um token. Aceita: 15h, 15h30, 15:30, 9h.
 * Retorna { horas, minutos } ou null.
 */
function parseHoraToken(token) {
  const t = (token || "").toLowerCase();
  if (!t) return null;

  const comMinutos = t.match(/^(\d{1,2})[h:](\d{2})$/);
  if (comMinutos) {
    const horas = Number(comMinutos[1]);
    const minutos = Number(comMinutos[2]);
    if (horas <= 23 && minutos <= 59) return { horas, minutos };
    return null;
  }

  const soHora = t.match(/^(\d{1,2})h$/);
  if (soHora) {
    const horas = Number(soHora[1]);
    if (horas <= 23) return { horas, minutos: 0 };
  }

  return null;
}

function ehTokenIgnoravel(token) {
  return ["as", "às", "as.", "para", "pra"].includes((token || "").toLowerCase());
}

/**
 * Interpreta o texto livre da captura e separa o que e lembrete do que e horario.
 *
 * Regras deliberadas:
 *  - duracao relativa (em 30min)  -> agora + duracao, e tem prioridade
 *  - so hora, sem data            -> hoje; se ja passou, amanha
 *  - so data, sem hora            -> 09:00 daquele dia
 *  - nada                         -> sem alarme, so caixa de entrada
 */
export function parseReminderInput(input) {
  const raw = (input || "").trim();
  const tokens = raw.split(/\s+/).filter(Boolean);

  let ymd = null;
  let hora = null;
  let minutosRelativos = null;
  const textoParts = [];

  let i = 0;

  while (i < tokens.length) {
    const token = tokens[i];

    if (minutosRelativos === null) {
      const relativo = lerDuracaoRelativa(tokens, i);
      if (relativo) {
        minutosRelativos = relativo.minutos;
        i += relativo.consumidos;
        continue;
      }
    }

    const dataToken = parseDateToken(token);
    if (dataToken && !ymd) {
      ymd = dataToken;
      i += 1;
      continue;
    }

    const horaToken = parseHoraToken(token);
    if (horaToken && !hora) {
      hora = horaToken;
      // "as" / "às" antes da hora vira ruido no texto final.
      if (ehTokenIgnoravel(textoParts[textoParts.length - 1])) {
        textoParts.pop();
      }
      i += 1;
      continue;
    }

    textoParts.push(token);
    i += 1;
  }

  const texto = textoParts.join(" ").trim();

  // Duracao relativa manda: quem escreve "em 30min" quer 30 minutos a partir
  // de agora, independente de qualquer outra data no texto.
  if (minutosRelativos !== null) {
    const alvo = new Date();
    alvo.setSeconds(0, 0);
    alvo.setMinutes(alvo.getMinutes() + minutosRelativos);
    return { texto, lembrarEm: alvo };
  }

  if (!ymd && !hora) {
    return { texto, lembrarEm: null };
  }

  const base = ymd ? new Date(`${ymd}T00:00:00`) : new Date();

  if (hora) {
    base.setHours(hora.horas, hora.minutos, 0, 0);
  } else {
    base.setHours(HORA_PADRAO, 0, 0, 0);
  }

  // Hora solta que ja passou hoje quase sempre significa amanha.
  if (!ymd && base.getTime() <= Date.now()) {
    base.setDate(base.getDate() + 1);
  }

  return { texto, lembrarEm: base };
}

export function formatarLembrarEm(valor) {
  if (!valor) return null;

  const data = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(data.getTime())) return null;

  const hoje = new Date();
  const amanha = new Date();
  amanha.setDate(amanha.getDate() + 1);

  const mesmoDia = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const hhmm = data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  if (mesmoDia(data, hoje)) return `Hoje ${hhmm}`;
  if (mesmoDia(data, amanha)) return `Amanha ${hhmm}`;

  return `${data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${hhmm}`;
}

export function estaVencido(valor) {
  if (!valor) return false;
  const data = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(data.getTime())) return false;
  return data.getTime() <= Date.now();
}

/** Converte um valor do banco no formato aceito por input[type=datetime-local]. */
export function paraInputDateTime(valor) {
  if (!valor) return "";

  const data = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(data.getTime())) return "";

  const p = (n) => String(n).padStart(2, "0");

  return `${data.getFullYear()}-${p(data.getMonth() + 1)}-${p(data.getDate())}T${p(
    data.getHours()
  )}:${p(data.getMinutes())}`;
}
